import React, { createContext } from "react";

import {messages as DA_DK} from "@apps/mf-overview/src/ts/i18n/locales/da-dk";

/**
 * Export locale specification publicly, so that typings can be imported
 * elsewhere without referencing locale data directly.
 *
 * We're using the default language (danish) as the type specification.
 */
export type Locale = typeof DA_DK;

/**
 * Creates a React context (ie. a Provider / Consumer pair), which allows us to
 * propagate references to the current locale all the way down through our
 * render tree.
 */

// @ts-ignore
// tslint:disable-next-line variable-name
const LocaleContext = createContext<Locale>();

// Provide a meaningful displayName for development environments
LocaleContext.displayName = "LocaleContext";

/**
 * The inject method is just a bit of syntaxtic sugar for injecting data from
 * the store into components.
 *
 * It allows definition of a callback, which will recieve a reference to the
 * store defined in the provider, along with any props given when the component
 * is mounted into the DOM.
 */
export function injectLocale<P>(callback: (locale: Locale, props: P) => React.JSX.Element): React.FC<P> {
    // Create a stateless functional wrapper for the component, which is going
    // to inject the store to the callback using the consumer from the store
    // context
    const wrapper: React.FC<P> = (props: P) => (
        <LocaleContext.Consumer>
            {(value: Locale) => callback(value, props)}
        </LocaleContext.Consumer>
    );

    // Provide a meaningful displayName for development environments
    wrapper.displayName = "InjectLocale";

    return wrapper;
}

/**
 * Expose a reference to the LocaleProvider publicly, so that the Root component
 * can mount it and thereby provide the reference globally within it's render
 * tree.
 */
// tslint:disable-next-line variable-name
export const LocaleProvider = LocaleContext.Provider;
